import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export const GalleryManager = () => {
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ url: "", caption: "" });

  const load = async () => {
    const { data } = await api.get("/gallery");
    setImages(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/gallery/${editing.id}`, form);
      else await api.post("/admin/gallery", form);
      toast.success(editing ? "Image replaced" : "Image added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (img) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/admin/gallery/${img.id}`);
    toast.success("Image deleted");
    load();
  };

  return (
    <div data-testid="admin-gallery-manager">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-cream">Gallery <span className="text-sm text-muted-foreground">({images.length})</span></h2>
        <Button data-testid="admin-add-image-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel"
          onClick={() => { setEditing(null); setForm({ url: "", caption: "" }); setOpen(true); }}>
          <Plus className="mr-1.5 h-4 w-4" /> Add Image
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {images.map((img) => (
          <div key={img.id} data-testid={`admin-gallery-item-${img.id}`}
            className="overflow-hidden rounded-xl border border-border bg-card">
            <img src={img.url} alt={img.caption} className="h-32 w-full object-cover" />
            <div className="flex items-center justify-between gap-1 p-2">
              <span className="truncate text-xs text-muted-foreground">{img.caption || "No caption"}</span>
              <div className="flex">
                <Button size="icon" variant="ghost" data-testid={`admin-replace-image-${img.id}`}
                  onClick={() => { setEditing(img); setForm({ url: img.url, caption: img.caption }); setOpen(true); }}>
                  <Pencil className="h-4 w-4 text-cream/80" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(img)} data-testid={`admin-delete-image-${img.id}`}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-image-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Replace Image" : "Add Image"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Image URL</Label>
              <Input value={form.url} required data-testid="image-url-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </div>
            <div>
              <Label>Caption</Label>
              <Input value={form.caption} data-testid="image-caption-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="image-save-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
